import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { formsApi } from '@/api/formsApi'
import { patientsApi, type PatientListItem } from '@/api/patientsApi'
import type { FormDefinition, FormSubmission } from '@/types/cgt'

/** One row per form family: the newest active version, as a picker should offer. */
function latestPerFamily(forms: FormDefinition[]): FormDefinition[] {
  const latest = new Map<string, FormDefinition>()
  for (const f of forms) {
    const key = f.familyId ?? f.id
    const kept = latest.get(key)
    if (!kept || (f.version ?? 1) > (kept.version ?? 1)) latest.set(key, f)
  }
  return [...latest.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Start a Collection form for a patient, from the Collection page (z8nrz7f9j7).
 *
 * Offers the newest active version of each form whose Form Type is
 * Collection. Not ad hoc: the server still applies the form's own predecessor
 * check, as it does for any standalone form.
 */
const PATIENTS_ERROR = 'Could not load patients.'

export function StartCollectionFormDialog({
  open,
  onClose,
  onStarted,
}: {
  open: boolean
  onClose: () => void
  onStarted: (submission: FormSubmission) => void
}) {
  const [mrn, setMrn] = useState('')
  // The picked patient's label, kept because a later search can drop that
  // patient from the options and the trigger would fall back to the placeholder.
  const [picked, setPicked] = useState<{ value: string; label: string } | undefined>()
  const [formId, setFormId] = useState('')
  const [patientSearch, setPatientSearch] = useState('')
  const [patients, setPatients] = useState<PatientListItem[]>([])
  const [forms, setForms] = useState<FormDefinition[]>([])
  const [loadingForms, setLoadingForms] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMrn('')
    setPicked(undefined)
    setFormId('')
    setPatientSearch('')
    setError(null)
    setLoadingForms(true)
    formsApi
      .listDefinitions({ category: 'collection', active: true })
      .then(({ items }) => setForms(latestPerFamily(items)))
      .catch(() => setError('Could not load the Collection forms.'))
      .finally(() => setLoadingForms(false))
  }, [open])

  // Patients are searched on the server: a tenant can hold thousands.
  useEffect(() => {
    if (!open) return
    let current = true
    const id = setTimeout(() => {
      patientsApi
        .list({ q: patientSearch.trim() || undefined, take: 50 })
        .then(({ items }) => {
          if (!current) return
          setPatients(items)
          setError(prev => (prev === PATIENTS_ERROR ? null : prev))
        })
        .catch(() => {
          if (current) setError(PATIENTS_ERROR)
        })
    }, 250)
    return () => {
      current = false
      clearTimeout(id)
    }
  }, [open, patientSearch])

  const patientOptions = useMemo(
    () =>
      patients.map(p => ({
        value: p.mrn,
        label: p.fullName ? `${p.mrn} · ${p.fullName}` : p.mrn,
      })),
    [patients],
  )
  const formOptions = useMemo(
    () => forms.map(f => ({ value: f.id, label: `${f.name} (V${f.version ?? 1})` })),
    [forms],
  )

  const canSubmit = !!mrn && !!formId && !saving

  async function submit() {
    if (!mrn || !formId) return
    setSaving(true)
    setError(null)
    try {
      const submission = await formsApi.createSubmission({
        formDefinitionId: formId,
        patientMrn: mrn,
      })
      onStarted(submission)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start that form.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start Collection form</DialogTitle>
          <DialogDescription>Pick the patient, then the Collection form to start.</DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>
              Patient <span className='text-red-600'>*</span>
            </label>
            <SearchableSelect
              value={mrn}
              onChange={value => {
                setMrn(value)
                setPicked(patientOptions.find(o => o.value === value))
              }}
              options={patientOptions}
              selectedOption={picked}
              onSearchChange={setPatientSearch}
              placeholder='Search by MRN or name…'
              emptyMessage='No patients found.'
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>
              Collection form <span className='text-red-600'>*</span>
            </label>
            <SearchableSelect
              value={formId}
              onChange={setFormId}
              options={formOptions}
              placeholder={
                loadingForms
                  ? 'Loading Collection forms…'
                  : forms.length
                    ? 'Select a Collection form…'
                    : 'No Collection forms yet'
              }
              disabled={loadingForms || !forms.length}
              emptyMessage='No matching forms.'
            />
            {!loadingForms && forms.length === 0 && (
              <p className='text-xs text-muted-foreground'>
                Create a form with Form Type <span className='font-medium'>Collection</span> in the Form Builder first.
              </p>
            )}
          </div>

          {error && <p className='text-sm font-medium text-red-600'>{error}</p>}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {saving && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Start form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
